import { useState, useEffect, FC, useContext, useRef } from "react";
import { AppConfigContext } from "../context/AppConfigContext";
import { Modal, TextField, Icon, ResourceList, ResourceItem, Checkbox, Thumbnail, Spinner } from "@shopify/polaris";
import { ImageIcon, SearchIcon } from "@shopify/polaris-icons";
import useDebounce from "../hooks/useDebounce";
import { currencyToSymbols, moneyFormatter } from "../apps/utilities";
import {} from "@shopify/app-bridge";

// Define types for our resources and props
interface Resource {
    id: string;
    title: string;
    description?: string;
    image?: string;
    [key: string]: any;
}

type ResourceType = "products" | "collections" | "pages" | "blogs" | "articles" | "customers";

interface CustomResourcePickerProps {
    resourceType?: ResourceType;
    initialSelections?: string[];
    setOpen: (open: boolean) => void;
    onSelect: (resources: any[] | null) => void;
    query?: string;
    args?: any;
}

const CustomResourcePicker = () => {
    const [open, setOpen] = useState(false);
    const [args, setArgs] = useState<any>(null);
    const resolveRef = useRef<any>(null);

    useEffect(() => {
        const originalResourcePicker = shopify.resourcePicker;

        shopify.resourcePicker = async (args: any): Promise<any> => {
            if (!args.custom) {
                return await originalResourcePicker(args);
            } else {
                setArgs(args);
                const result = new Promise((res) => {
                    resolveRef.current = res;
                });
                setOpen(true);

                return await result;
            }
        };
    }, []);

    return (
        <>
            {open ? (
                <CustomResourcePickerModal
                    setOpen={setOpen}
                    initialSelections={args?.selectionIds?.map((i: any) => i.id) || []}
                    onSelect={resolveRef.current}
                    args={args}
                    query={args.query}
                />
            ) : null}
        </>
    );
};

const CustomResourcePickerModal: FC<CustomResourcePickerProps> = ({
    resourceType = "products",
    initialSelections = [],
    setOpen = () => {},
    onSelect = () => {},
    query = "",
    args = {},
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>(query);
    const [resources, setResources] = useState<Resource[]>([]);
    const [selectedResources, setSelectedResources] = useState<string[]>(initialSelections);
    const [cursor, setCursor] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const { sft } = useContext(AppConfigContext);
    const debouncedSearch = useDebounce(searchQuery, 400);

    // Initial fetch and when search changes
    useEffect(() => {
        setCursor(null); // Reset cursor when search changes
        fetchResources();
    }, [debouncedSearch]);

    // Fetch resources using Shopify Admin API via App Bridge
    const fetchResources = async (): Promise<void> => {
        setIsLoading(true);

        try {
            // Otherwise, use the Storefront API via fetch
            const storefrontAccessToken = sft;

            if (!storefrontAccessToken) {
                throw new Error("Missing Storefront API access token or shop domain");
            }

            // Build the appropriate GraphQL query based on resource type
            let query = "";
            const variables: Record<string, any> = {
                first: 20,
                searchQuery: searchQuery || null,
            };

            if (cursor) {
                variables.after = cursor;
            }

            switch (resourceType) {
                case "products":
                    query = `
            query Products($first: Int!, $after: String, $searchQuery: String) {
              products(first: $first, after: $after, query: $searchQuery) {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                edges {
                  cursor
                  node {
                    id
                    title
                    description
                    options{
                        id
                        name
                        values
                    }
                    featuredImage {
                      url
                    }
                    handle
                    metafield(namespace: "kefi_swatches", key: "swatches") {
                        value
                        type
                    }
                    variants(first: 250) {
                      edges {
                        node {
                          id
                          title
                          price {
                            amount
                            currencyCode
                          }
                        compareAtPrice {
                               amount
                        }
                            quantityAvailable
                          availableForSale
                          image {
                            url
                         }
                             selectedOptions {
                                name
                                value
                            }


                        }
                      }
                    }
                  }
                }
              }
            }
          `;
                    break;

                default:
                    throw new Error(`Unsupported resource type: ${resourceType}`);
            }

            // Make the GraphQL request to the Storefront API
            const response = await fetch(`https://${shopify.config.shop}/api/2025-01/graphql.json`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
                },
                body: JSON.stringify({
                    query,
                    variables,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();

            // Process the response based on resource type
            let newResources: Resource[] = [];
            let newCursor: string | null = null;
            let newHasNextPage = false;
            if (data.data) {
                const resourceData = data.data[resourceType];

                if (resourceData) {
                    newHasNextPage = resourceData.pageInfo.hasNextPage;
                    newCursor = resourceData.pageInfo.endCursor;

                    newResources = resourceData.edges.map((edge: any) => {
                        const node = edge.node;

                        // Create a standardized resource object
                        const resource: Resource = {
                            id: node.id,
                            title: node.title,
                            description: node.description || "",
                            handle: node.handle,
                            cursor: edge.cursor,
                            metafield: node.metafield,
                            options: node.options || [],
                            variants: node.variants.edges.map((edge: any) => {
                                if (node.variants.edges.length == 1) {
                                    edge.node.title = node.title;
                                }
                                return edge.node;
                            }),
                        };

                        // Add image if available
                        if (resourceType === "products" && node.featuredImage) {
                            resource.image = node.featuredImage.url;
                        } else if (node.image) {
                            resource.image = node.image.url;
                        }

                        // Add price for products
                        if (resourceType === "products" && node.variants?.edges?.length > 0) {
                            resource.price = node.variants.edges[0].node.price.amount;
                        }

                        return resource;
                    });
                }
            }

            setResources((prevResources) => (cursor ? [...prevResources, ...newResources] : newResources));
            setCursor(newCursor);
            setHasNextPage(newHasNextPage);
        } catch (error) {
            console.error("Error fetching resources:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch more when "Load more" is clicked
    const loadMore = () => {
        if (cursor && hasNextPage) {
            fetchResources();
        }
    };

    // Handle resource selection
    const toggleSelection = (checked: boolean, id: string): void => {
        if (args.multiple === false) {
            // Single product select mode
            setSelectedResources(checked ? [id] : []);
        } else {
            // Multi-variant selection mode
            if (!checked) {
                setSelectedResources(selectedResources.filter((item) => item !== id));
            } else {
                setSelectedResources([...selectedResources, id]);
            }
        }
    };

    // Check if a resource is selected
    const isSelected = (resourceId: string): boolean => {
        return selectedResources.some((item) => item === resourceId);
    };

    // Handle save button click
    const handleSelect = (): void => {
        const selected: any[] = [];

        if (args.multiple === false) {
            const selectedProduct = resources.find((res) => isSelected(res.id));
            if (selectedProduct) selected.push(selectedProduct);
        } else {
            resources.forEach((resource) => {
                resource.variants.forEach((variant: any) => {
                    if (isSelected(variant.id)) {
                        if (resource.variants.length > 1) {
                            variant.title = resource.title + " - " + variant.title;
                        }
                        selected.push(variant);
                    }
                });
            });
        }

        onSelect(selected);
        setOpen(false);
    };

    useEffect(() => {
        return () => {
            onSelect(null);
        };
    }, []);
    console._log("resources", resources);

    return (
        <>
            <Modal
                onScrolledToBottom={loadMore}
                open={true}
                title="Select products"
                onClose={() => setOpen(false)}
                primaryAction={{
                    content: "Select",
                    onAction: handleSelect,
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: () => setOpen(false),
                    },
                ]}
                footer={<span>{selectedResources.length} products selected</span>}
            >
                <div
                    style={{
                        position: "sticky",
                        top: 0,
                        background: "white",
                        zIndex: 10,
                        paddingTop: "20px",
                        width: "95%",
                        margin: "auto",
                    }}
                >
                    <TextField
                        placeholder="Search products"
                        label=""
                        value={searchQuery}
                        onChange={(value) => {
                            setSearchQuery(value);
                            setCursor(null);
                        }}
                        clearButton
                        onClearButtonClick={() => {
                            setCursor(null);
                            setSearchQuery("");
                        }}
                        autoComplete="off"
                        prefix={<Icon source={SearchIcon} />}
                    />
                </div>
                <Modal.Section>
                    <br />
                    {isLoading && !cursor ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                            <Spinner size="small" />
                        </div>
                    ) : (
                        <>
                            {args.multiple === false ? (
                                // Single product select mode
                                <ResourceList
                                    showHeader={false}
                                    resourceName={{ singular: "product", plural: "products" }}
                                    items={resources.map((resource) => ({
                                        id: resource.id,
                                        name: resource.title,
                                        location: resource.description,
                                        imgSrc: resource.image || "",
                                    }))}
                                    renderItem={(item) => {
                                        const { id, imgSrc, name } = item;
                                        const isCurrentSelected = isSelected(id);
                                        const isOtherSelected = !isCurrentSelected && selectedResources.length > 0;
                                        return (
                                            <ResourceItem
                                                url=""
                                                id={id}
                                                disabled={isOtherSelected} // disable if another item is selected
                                                onClick={() => {
                                                    console._log("Item clicked:", item);

                                                    toggleSelection(!isSelected(id), id);
                                                }} // handle selection
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: 20,
                                                        alignItems: "center",
                                                        color: isOtherSelected ? "rgba(181, 181, 181, 1)" : "inherit",
                                                        opacity: isOtherSelected ? 0.6 : 1,
                                                        pointerEvents: isOtherSelected ? "none" : "auto",
                                                    }}
                                                >
                                                    <Checkbox label="" id={id} checked={isSelected(id)} />
                                                    <Thumbnail source={imgSrc || ImageIcon} size="small" alt={name} />
                                                    {name}
                                                </div>
                                            </ResourceItem>
                                        );
                                    }}
                                    hasMoreItems={hasNextPage}
                                    loading={isLoading}
                                />
                            ) : (
                                // Default variant-level multiple select
                                <ResourceList
                                    showHeader={false}
                                    resourceName={{ singular: "customer", plural: "customers" }}
                                    items={resources.map((resource) => ({
                                        id: resource.id,
                                        name: resource.title,
                                        location: resource.description,
                                        imgSrc: resource.image || "",
                                        variants: resource.variants,
                                    }))}
                                    renderItem={(item) => {
                                        const { id, imgSrc, name } = item;
                                        return (
                                            <ResourceItem url="" id={id}>
                                                <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
                                                    <Thumbnail source={imgSrc || ImageIcon} size="small" alt={name} />
                                                    {name}
                                                </div>
                                                <ResourceList
                                                    showHeader={false}
                                                    items={item.variants.map((variant: any) => ({
                                                        id: variant.id,
                                                        name: variant.title,
                                                        imgSrc: variant.image?.url || "",
                                                        price: variant.price,
                                                        quantityAvailable: variant.quantityAvailable,
                                                    }))}
                                                    renderItem={(item) => {
                                                        const { id, imgSrc, name, price, quantityAvailable } = item;

                                                        return (
                                                            <ResourceItem
                                                                url=""
                                                                id={id}
                                                                accessibilityLabel={`View details for ${name}`}
                                                                onClick={() => toggleSelection(!isSelected(id), id)}
                                                            >
                                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                                    <Checkbox
                                                                        label=""
                                                                        id={id}
                                                                        checked={isSelected(id)}
                                                                    />
                                                                    <Thumbnail
                                                                        source={imgSrc || ImageIcon}
                                                                        size="small"
                                                                        alt={name}
                                                                    />
                                                                    <span style={{ marginLeft: 10 }}>{name}</span>
                                                                    <span
                                                                        style={{ marginLeft: "auto", marginRight: 50 }}
                                                                    >
                                                                        {quantityAvailable} available
                                                                    </span>
                                                                    <span>
                                                                        {
                                                                            currencyToSymbols[
                                                                                price?.currencyCode as keyof typeof currencyToSymbols
                                                                            ]
                                                                        }
                                                                        {moneyFormatter.format(price?.amount)}
                                                                    </span>
                                                                </div>
                                                            </ResourceItem>
                                                        );
                                                    }}
                                                />
                                            </ResourceItem>
                                        );
                                    }}
                                    hasMoreItems={hasNextPage}
                                    loading={isLoading}
                                />
                            )}
                            {isLoading && (
                                <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                                    <Spinner size="small" />
                                </div>
                            )}
                        </>
                    )}
                </Modal.Section>
            </Modal>
        </>
    );
};

export default CustomResourcePicker;
